'use client';

import { cn } from '@shared/components/shadcn';
import { ApplePay, Ellipse } from '@shared/icons';
import { useTranslations } from 'next-intl';

type PaymentMethodSelectorItemProps = {
  namespace?: string;
  icon: React.ReactNode;
  name: string;
  isSelected: boolean;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  'data-track-name'?: string;
  'data-track-method'?: string;
  'data-track-position'?: number;
  type?: 'instrument' | 'method';
  isApplePay?: boolean;
  expiryMonth?: string;
  expiryYear?: string;
};

function PaymentMethodSelectorItem({
  icon,
  name,
  isSelected,
  onClick,
  className,
  disabled,
  type,
  isApplePay,
  namespace,
  expiryMonth,
  expiryYear,
  ...props
}: PaymentMethodSelectorItemProps) {
  const isInstrument = type === 'instrument';
  const t = useTranslations(`${namespace}.payment_method_selector`);
  return (
    <button
      className={cn('flex gap-3 p-3 rounded-lg border border-border bg-container-base', isSelected && 'border-border-active bg-button-background', className)}
      onClick={onClick}
      disabled={disabled}
      data-testid={`payment-method-selector-${name}`}
      {...props}
    >
      <div className="flex flex-col items-start gap-2">
        <div className={cn('flex gap-1', isSelected && 'text-foreground-inverse')}>
          {icon}
          {isApplePay && (
            <div className="w-px py-1">
              <div className={cn('w-full h-full bg-foreground', isSelected && 'bg-foreground-inverse')} />
            </div>
          )}
          {isApplePay && <ApplePay size="medium" />}
        </div>
        <div className={cn('paragraph-s-strong whitespace-nowrap', isSelected && 'text-foreground-inverse')}>
          {isInstrument ? (
            <div className="flex gap-1 items-center">
              <div className="flex gap-0.5 items-center" data-track-privacy="Mask">
                {isApplePay ? (
                  <span className="whitespace-nowrap">
                    {t('expires_date', { month: expiryMonth ?? '', year: expiryYear?.length === 4 ? expiryYear.slice(2) : (expiryYear ?? '') })}
                  </span>
                ) : (
                  <>
                    {Array.from({ length: 4 }).map((_, index) => (
                      <Ellipse key={index} size="extra-tiny" className="shrink-0" />
                    ))}{' '}
                    <span>{name}</span>
                  </>
                )}
              </div>
            </div>
          ) : (
            name
          )}
        </div>
      </div>
    </button>
  );
}

export { PaymentMethodSelectorItem };
export type { PaymentMethodSelectorItemProps };
